import { formatAed } from '@shared/utils'

// Token-quantity rules for the invest drawer's Investment Details card —
// mirrors the mobile app. Pure functions so every rule is unit-tested
// (__tests__/tokenQuantityUtils.spec.ts).

const PERCENT_BASE = 100

const formatCount = (count: number): string => count.toLocaleString('en-US')

/** Digits only, no leading zeros ("00abc12" → "12"). */
export const sanitizeTokenDraft = (raw: string): string =>
  raw.replace(/[^0-9]/g, '').replace(/^0+/, '')

export const clampTokens = (value: number, min: number, max: number): number =>
  Math.min(Math.max(Math.round(value), min), Math.max(max, min))

/** Slider's upper end: the per-investor cap, limited by stock, never below min. */
export const sliderMaxFor = (min: number, max: number, tokensAvailable: number): number =>
  Math.max(Math.min(max, tokensAvailable), min)

/** Inline message under the count row while the typed draft is out of range. */
export const draftRangeError = (draft: string, min: number, max: number): string | null => {
  if (!draft) return null
  const typed = Number(draft)
  if (typed > max) return `Maximum limit is ${formatCount(max)} tokens.`
  if (typed < min) return `Minimum limit is ${formatCount(min)} tokens.`
  return null
}

/** Blur / Enter: clamp the draft; an empty or unreadable draft keeps the previous value. */
export const commitTokenDraft = (
  draft: string,
  previous: number,
  min: number,
  max: number,
): number => {
  const typed = Number(draft)
  if (!draft || !Number.isFinite(typed)) return previous
  return clampTokens(typed, min, max)
}

export const canStepDown = (value: number, min: number): boolean => value > min
export const canStepUp = (value: number, max: number): boolean => value < max

/**
 * A higher minimum the API asked for on a rejected quote — `minTokens`
 * directly, or `minInvestmentAmount` converted at the token price. Looks at
 * the error body's top level and its `data` envelope. Null when absent.
 */
export const minTokensFromError = (details: unknown, tokenPrice: number): number | null => {
  const read = (source: unknown, key: string): number | null => {
    if (!source || typeof source !== 'object' || !(key in source)) return null
    const value = Number(Reflect.get(source, key))
    return Number.isFinite(value) && value > 0 ? value : null
  }
  const envelope =
    details && typeof details === 'object' && 'data' in details
      ? details.data
      : null
  const minTokens = read(details, 'minTokens') ?? read(envelope, 'minTokens')
  if (minTokens) return Math.ceil(minTokens)
  const minAmount = read(details, 'minInvestmentAmount') ?? read(envelope, 'minInvestmentAmount')
  return minAmount && tokenPrice > 0 ? Math.ceil(minAmount / tokenPrice) : null
}

/** VALUE row: the server's gross while it matches the count, else tokens × price. */
export const investmentValueFor = (
  tokens: number,
  tokenPrice: number,
  quote: { tokenQuantity: number; grossAmount: number } | null,
): number => (quote && quote.tokenQuantity === tokens ? quote.grossAmount : tokens * tokenPrice)

/** "19.35%" — value over the Sliceal price, else tokens over supply. Null when neither is known. */
export const ownershipShareFor = (
  value: number,
  slicealPrice: number | null,
  tokens: number,
  totalTokens: number,
): string | null => {
  if (slicealPrice && slicealPrice > 0)
    return `${((value / slicealPrice) * PERCENT_BASE).toFixed(2)}%`
  if (totalTokens > 0) return `${((tokens / totalTokens) * PERCENT_BASE).toFixed(2)}%`
  return null
}

export interface OrderValidationInput {
  apiError: string | null
  tokens: number
  tokenPrice: number
  minTokens: number
  tokensAvailable: number
  minInvestmentAmount: number | null
  maxInvestmentAmount: number | null
  /** Only checked when the wallet is the selected payment method. */
  wallet: { isActive: boolean; balance: number } | null
  totalPayable: number
}

/** First blocking problem with the order, or null. Any message disables Continue. */
export const orderValidationError = (input: OrderValidationInput): string | null => {
  const { apiError, tokens, tokenPrice, minTokens, tokensAvailable, wallet } = input
  const value = tokens * tokenPrice
  if (apiError) return apiError
  if (tokensAvailable < minTokens) {
    return `Only ${formatCount(tokensAvailable)} tokens remaining — not enough to meet the minimum investment of ${formatAed(minTokens * tokenPrice)}.`
  }
  if (tokens > tokensAvailable) {
    return `Only ${formatCount(tokensAvailable)} tokens available (${formatAed(tokensAvailable * tokenPrice)}).`
  }
  const { maxInvestmentAmount: max, minInvestmentAmount: min } = input
  if (max && tokenPrice > 0 && value > max) {
    return `Maximum investment is ${formatAed(max)} (${formatCount(Math.floor(max / tokenPrice))} tokens).`
  }
  if (min && tokenPrice > 0 && value < min) {
    return `Minimum investment is ${formatAed(min)} (${formatCount(Math.ceil(min / tokenPrice))} tokens).`
  }
  if (wallet && !wallet.isActive) return 'Your wallet is not active yet.'
  if (wallet && wallet.balance < input.totalPayable) {
    return `Insufficient wallet balance. Your balance is ${formatAed(wallet.balance)}.`
  }
  return null
}
